import {
  LocalStorageKeys,
  LocalStorageService,
} from 'src/services/localStorage';
import Cookies from 'universal-cookie';

const EMBEDDED_WEBVIEW_PARAM = 'embeddedWebViewData';
const EMBEDDED_WEBVIEW_SESSION_KEY = '__EMBEDDED_WEBVIEW__';

/**
 * Sets language in localStorage BEFORE i18n initializes.
 * Must be called synchronously before the i18n import since i18n reads
 * from localStorage at import time via LanguageDetector.
 */
export const preInitEmbeddedWebviewLanguage = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(EMBEDDED_WEBVIEW_PARAM);
    if (!encoded) return;

    const decoded = JSON.parse(window.atob(encoded));
    const language = decoded['accept-language'];
    if (language) {
      localStorage.setItem('lng', language);
    }
  } catch (e) {
    // Silently catch — full init happens later in initEmbeddedWebview
    console.error('Error in preInitEmbeddedWebviewLanguage:', e);
  }
};

/**
 * Injects auth credentials from the embeddedWebViewData URL parameter
 * into encrypted localStorage and marks the session as embedded.
 * Returns true on success, false if no param or parse error.
 */
export const initEmbeddedWebview = async (): Promise<boolean> => {
  try {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(EMBEDDED_WEBVIEW_PARAM);
    if (!encoded) {
      return false;
    }

    let decoded: any;
    try {
      decoded = JSON.parse(window.atob(encoded));
    } catch (e) {
      return false;
    }

    const userId = decoded['user-id'];
    const accessToken = decoded['access-token'];
    const organisationId = decoded['organisation-id'];
    if (!userId || !accessToken || !organisationId) return false;

    await LocalStorageService.set(LocalStorageKeys.___USER_ID__, userId);
    await LocalStorageService.set(LocalStorageKeys.__AUTH_TOKEN__, accessToken);
    await LocalStorageService.set(
      LocalStorageKeys.__ORGANISATION_ID__,
      organisationId,
    );

    const cookies = new Cookies();
    cookies.set('is_authenticated', 'true', {
      path: '/',
      expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365),
      secure: true,
      sameSite: 'lax',
    });

    sessionStorage.setItem(EMBEDDED_WEBVIEW_SESSION_KEY, 'true');

    // Remove credentials from the URL
    window.history.replaceState({}, '', window.location.pathname);

    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Returns true if the current session is running inside an embedded WebView.
 */
export const isEmbeddedWebview = (): boolean => {
  return sessionStorage.getItem(EMBEDDED_WEBVIEW_SESSION_KEY) === 'true';
};
